import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import type { TableRowPropsWithData } from '@openx/components-table/parts/TableRow.tsx';
import { TableRow as OpenXTableRow } from '@openx/components-table/parts/TableRow.tsx';
import { copyToClipboardWithConfirmation } from '@openx/utils/lib/copyToClipboard.ts';
import JsonView from '@uiw/react-json-view';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ErrorRow } from '../DealDetails/hooks/useTddErrorTableColumns.tsx';

export type TddSyncExpandableTableRowProps = {
  isCollapsableJsonView: boolean;
  isForcedExpanded?: (data: ErrorRow) => boolean;
  rowProps: TableRowPropsWithData<ErrorRow>;
  toggleRowExpansion: (rowIndex: number) => void;
};

export const TddSyncExpandableTableRow = ({
  isForcedExpanded,
  rowProps,
  isCollapsableJsonView,
  toggleRowExpansion,
}: TddSyncExpandableTableRowProps) => {
  const { rowData } = rowProps;
  const { key, ...restRowProps } = rowProps;
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const isExpanded = isForcedExpanded ? isForcedExpanded(rowData) : false;

  const closeRow = useCallback(() => {
    toggleRowExpansion(rowData.index);
  }, [rowData.index, toggleRowExpansion]);

  const onRowClick = useCallback(() => {
    toggleRowExpansion(rowData.index);
  }, [rowData.index, toggleRowExpansion]);

  const errorDataForDisplay = useMemo(() => {
    const { index: _index, expanded: _expanded, ...errorData } = rowData;
    return errorData;
  }, [rowData]);

  const copyContent = useCallback(() => {
    copyToClipboardWithConfirmation(JSON.stringify(errorDataForDisplay, null, 2), enqueueSnackbar, t);
  }, [errorDataForDisplay, enqueueSnackbar, t]);

  const jsonView = isCollapsableJsonView ? (
    <JsonView
      className="data-test-json-view-tree"
      collapsed={1}
      enableClipboard
      indentWidth={4}
      value={errorDataForDisplay}
    />
  ) : (
    <pre data-test="json-view-full" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
      {JSON.stringify(errorDataForDisplay, null, 2)}
    </pre>
  );

  return (
    <>
      <OpenXTableRow
        key={key}
        {...restRowProps}
        isClickable={false}
        onRowClick={onRowClick}
        rowData={{ ...rowData, expanded: isExpanded } as ErrorRow}
        sx={{
          cursor: 'pointer',
          '& .MuiTableCell-root': {
            padding: theme => theme.spacing(0, 1),
          },
        }}
      />
      <TableRow
        sx={{
          backgroundColor: theme => alpha(theme.palette.common.black, 0.02),
        }}
      >
        <TableCell
          colSpan={rowProps.columns.length}
          sx={{
            border: 0,
            height: 'auto',
            padding: 0,
          }}
        >
          <Collapse in={isExpanded} timeout={350} unmountOnExit>
            <Box
              data-test="expanded-row"
              sx={{
                p: 2,
              }}
            >
              {jsonView}
              <Stack
                direction="row"
                sx={{
                  marginTop: 2,
                  marginBottom: 1,
                  gap: 1,
                }}
              >
                <Button onClick={closeRow} variant="text">
                  {t('Close')}
                </Button>
                <Button onClick={copyContent} size="small">
                  {t('Copy To clipboard')}
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
